import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { Toaster } from 'sonner';
import ScrollToTop from './components/ScrollToTop';
import GoogleAnalytics from './components/GoogleAnalytics';
import CookieBanner from './components/CookieBanner';
import DocumentTitle from './components/DocumentTitle';
import './App.css';
import { TERMINAL_ENABLED } from './config/features';
// Home loads immediately (eager)
import Home from './pages/Home';

// Everything else loads on demand (lazy)
const AboutMe = lazy(() => import('./pages/AboutMe'));
const Strategies = lazy(() => import('./pages/Strategies'));
const StrategyDetail = lazy(() => import('./pages/StrategyDetail'));
const Yggdrasil = lazy(() => import('./pages/Yggdrasil'));
const Performance = lazy(() => import('./pages/Performance'));
const PortfolioBuilder = lazy(() => import('./pages/PortfolioBuilder'));
const Insights = lazy(() => import('./pages/Insights'));
const WeeklyReports = lazy(() => import('./pages/WeeklyReports'));
const Terminal = lazy(() => import('./pages/Terminal'));
const TerminalLanding = lazy(() => import('./pages/TerminalLanding'));
const TerminalApp = lazy(() => import('./pages/TerminalApp'));
const Contact = lazy(() => import('./pages/Contact'));
const Institutional = lazy(() => import('./pages/Institutional'));
const Login = lazy(() => import('./pages/Login'));
const Account = lazy(() => import('./pages/Account'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const LegalNotice = lazy(() => import('./pages/LegalNotice'));

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <DocumentTitle />
      <GoogleAnalytics />
      <CookieBanner />
      <Toaster
        theme="dark"
        position="bottom-center"
        duration={4000}
        toastOptions={{
          style: {
            background: 'rgba(10,10,18,0.98)',
            border: '1px solid rgba(0,255,200,0.25)',
            color: '#e8e8e8',
            fontFamily: "'Inter', sans-serif",
            fontSize: '13px',
          },
        }}
      />
      <Analytics />
      <div style={{ position: 'relative', zIndex: 2 }}>
        <Suspense fallback={
          <div style={{ 
            background: '#06060e', 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              border: '3px solid rgba(0,255,200,0.2)', 
              borderTop: '3px solid #00ffc8', 
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite' 
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        }>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<AboutMe />} />
            <Route path="/strategies" element={<Strategies />} />
            <Route path="/strategies/:slug" element={<StrategyDetail />} />
            <Route path="/yggdrasil" element={<Yggdrasil />} />
            <Route path="/ygdrasil" element={<Navigate to="/yggdrasil" replace />} />
            <Route path="/engine" element={<Navigate to="/yggdrasil" replace />} />
            <Route path="/performance" element={<Performance />} />
            <Route path="/portfolio" element={<PortfolioBuilder />} />
            <Route path="/portfolio-builder" element={<Navigate to="/portfolio" replace />} />
            <Route path="/research" element={<Insights />} />
            <Route path="/insights" element={<Navigate to="/research" replace />} />
            <Route path="/reports" element={<WeeklyReports />} />
            <Route path="/weekly-reports" element={<Navigate to="/reports" replace />} />
            <Route path="/lab" element={<Navigate to="/quant-lab" replace />} />
            <Route path="/quant-lab" element={<Terminal />} />
            <Route path="/terminal" element={TERMINAL_ENABLED ? <TerminalLanding /> : <Navigate to="/" replace />} />
            <Route path="/terminal/app" element={TERMINAL_ENABLED ? <TerminalApp /> : <Navigate to="/" replace />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/institutional" element={<Institutional />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/reset" element={<ResetPassword />} />
            <Route path="/account" element={<Account />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/legal" element={<LegalNotice />} />
          </Routes>
        </Suspense>
      </div>
    </BrowserRouter>
  );
}

export default App;
